/**
 * Exact item-placement loader for URL-driven overlays.
 *
 * One canonical item can be published in several organisations, so an item ID
 * alone is not enough authority. The browser must provide the item and
 * placement organisation returned by a bounded feed/presentation; the server
 * decides whether that exact placement is visible.
 */
import { ref } from "vue";

import {
  isItemAuthorityReadsFeatureEnabled,
  isStorageAuthorityReadsFeatureEnabled,
} from "@/config/runtimeConfig";
import { itemAuthorityService } from "@/services/itemAuthorityService";
import {
  createItemMediaUrlResolver,
  itemMediaUrlResolver,
} from "@/services/itemMediaUrlResolver";
import { mapAuthorityPlacementToMarketplaceCard } from "@/services/marketplaceFeedSource";

export async function fetchItemById(
  itemId,
  orgId,
  {
    authorityEnabled = isItemAuthorityReadsFeatureEnabled,
    storageEnabled = isStorageAuthorityReadsFeatureEnabled,
    authorityReader = itemAuthorityService,
    storageReader = null,
    mediaResolver = storageReader
      ? createItemMediaUrlResolver({
          reader: storageReader,
          enabled: storageEnabled,
        })
      : itemMediaUrlResolver,
  } = {},
) {
  if (!itemId || !orgId || !authorityEnabled) return null;

  const placement = await authorityReader.getItemPlacement({ itemId, orgId });
  // Revoked, private, missing or non-image assets are omitted. The item
  // presentation remains usable without exposing an object path.
  const images = storageEnabled
    ? await mediaResolver.resolveImageUrls(placement.mediaAssetIds)
    : Object.freeze([]);
  const card = mapAuthorityPlacementToMarketplaceCard(placement, images);
  return Object.freeze({
    ...card,
    image: images[0] || null,
  });
}

export function useItemFetch() {
  const item = ref(null);
  const loading = ref(false);
  const error = ref(null);

  const fetchItem = async (itemId, orgId) => {
    if (!itemId || !orgId) {
      item.value = null;
      error.value = null;
      return null;
    }
    loading.value = true;
    error.value = null;
    try {
      const result = await fetchItemById(itemId, orgId);
      item.value = result;
      if (!result) error.value = "Item not found";
      return result;
    } catch (caught) {
      error.value = caught?.message || "Failed to fetch item";
      item.value = null;
      return null;
    } finally {
      loading.value = false;
    }
  };

  return { item, loading, error, fetchItem };
}
